import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useQuery } from "urql"

import { practiceCtaText } from "../../@core/practice/practice.utils"

import FooterSection from "./components/FooterSection"
import HeaderSection from "./components/HeaderSection"
import ServiceAddOnModal from "./components/ServiceAddOnModal"
import ServiceAvailableTimes from "./components/ServiceAvailableTimes"
import ServiceDetails from "./components/ServiceDetails"
import ServiceLocation from "./components/ServiceLocation"
import ServicePractitioner from "./components/ServicePractitioner"
import ServiceTimeSearchBar from "./components/ServiceTimeSearchBar"
import { formatHeadingFontFamily } from "./utils/fontUtils"
import { pickDefaultBookingLocation } from "./utils/locationKindUtils"

const AVAILABILITY_QUERY = `
  query bookingAvailability($serviceId: ID!, $locationId: ID!, $page: Int) {
    bookingAvailability(serviceId: $serviceId, locationId: $locationId, page: $page) {
      timeZone
      availableSlots { date datetimes }
      lastDateInPage
    }
  }
`

const ServicePageV2Desktop = ({ practice, service, theme }) => {
  const [selectedDuration, setSelectedDuration] = useState(service)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [allAvailableTimes, setAllAvailableTimes] = useState([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedTime, setSelectedTime] = useState(null)
  const [showAddOnModal, setShowAddOnModal] = useState(false)
  const [pendingCheckoutTime, setPendingCheckoutTime] = useState(null)

  // Create mock section configurations for header and footer
  const headerSection = {
    id: "service-page-header",
    type: "header",
    settings: {
      showLogo: true,
      showCTA: true,
      ctaText: "Book Now",
      ctaAction: "booking",
      showNavigation: true
    }
  }

  const footerSection = {
    id: "service-page-footer",
    type: "footer",
    settings: {
      showLogo: true,
      showCTA: true,
      ctaText: practiceCtaText(practice),
      ctaAction: "booking",
      showSocialLinks: true,
      showPoweredBy: true
    }
  }

  // Set default selections based on service data
  useEffect(() => {
    // Set default duration (always start with the original service)
    setSelectedDuration({
      id: service.id,
      name: service.name,
      amountCents: service.amountCents,
      timeLength: service.timeLength
    })

    // Set default location: prefer in-person (office/physical) so the Location section shows when available
    if (service.locations && service.locations.length > 0) {
      setSelectedLocation(pickDefaultBookingLocation(service.locations) ?? service.locations[0])
    }
  }, [service])

  // Get the service ID to use for booking (could be main service or selected variation)
  const bookingServiceId = selectedDuration?.id || service.id

  // Fetch availability data from GraphQL
  const [{ data: availabilityData, fetching: loadingAvailability, error: availabilityError }, refetchAvailability] =
    useQuery({
      query: AVAILABILITY_QUERY,
      variables: {
        serviceId: bookingServiceId,
        locationId: selectedLocation?.id,
        page: currentPage
      },
      pause: !bookingServiceId || !selectedLocation?.id
    })

  const timeZone = availabilityData?.bookingAvailability?.timeZone
  const lastDateInPage = availabilityData?.bookingAvailability?.lastDateInPage

  // Calculate how many months of data we have
  const calculateMonthsLoaded = () => {
    if (!allAvailableTimes || allAvailableTimes.length === 0) return 0

    const firstDate = dayjs(allAvailableTimes[0].date)
    const lastDate = dayjs(allAvailableTimes[allAvailableTimes.length - 1].date)
    const monthsDiff = lastDate.diff(firstDate, "month", true)
    return Math.ceil(monthsDiff) + 1 // +1 to include the starting month
  }

  const monthsLoaded = calculateMonthsLoaded()
  const maxMonths = 6
  const hasReachedMaxMonths = monthsLoaded >= maxMonths

  // Accumulate available times when new data is loaded
  useEffect(() => {
    if (availabilityData?.bookingAvailability?.availableSlots) {
      // Save scroll position before updating state (only when loading more pages)
      const savedScrollPosition = currentPage > 0 ? window.pageYOffset || document.documentElement.scrollTop : null

      const newAvailableTimes = availabilityData.bookingAvailability.availableSlots.map((slot) => ({
        date: slot.date,
        times: slot.datetimes
      }))

      if (currentPage === 0) {
        // Reset for first page
        setAllAvailableTimes(newAvailableTimes)
      } else {
        // Append new times to existing ones
        setAllAvailableTimes((prev) => {
          const existingDates = new Set(prev.map((day) => day.date))
          const newTimes = newAvailableTimes.filter((day) => !existingDates.has(day.date))
          return [...prev, ...newTimes]
        })
      }
      // Reset loading more state
      setLoadingMore(false)

      // Restore scroll position after state update (only when loading more pages)
      if (savedScrollPosition !== null) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({
              top: savedScrollPosition,
              behavior: "instant"
            })
          })
        })
      }
    }
  }, [availabilityData, currentPage])

  // Reset pagination when location or duration changes
  useEffect(() => {
    setCurrentPage(0)
    setAllAvailableTimes([])
  }, [selectedLocation?.id, selectedDuration?.id])

  // Log availability errors
  if (availabilityError) {
    // eslint-disable-next-line no-console
    console.error("Availability query error:", availabilityError)
  }

  // Refetch availability when duration or location changes
  useEffect(() => {
    if (selectedDuration && selectedLocation) {
      refetchAvailability({ requestPolicy: "network-only" })
    }
  }, [selectedDuration, selectedLocation, currentPage, refetchAvailability])

  // Determine if service has multiple durations or locations (main service + any variation = 2+ options)
  const hasMultipleDurations = service.variations && service.variations.length > 0
  const hasMultipleLocations = service.locations && service.locations.length > 1

  // Handle duration change with refetch
  const handleDurationChange = (newDuration) => {
    setSelectedDuration(newDuration)
  }

  // Handle location change with refetch
  const handleLocationChange = (newLocation) => {
    setSelectedLocation(newLocation)
  }

  // Handle loading more times
  const handleLoadMoreTimes = () => {
    // Don't load more if we've reached 6 months
    if (hasReachedMaxMonths) {
      return
    }
    setLoadingMore(true)
    setCurrentPage((prev) => prev + 1)
  }

  // Handle time selection
  const handleTimeSelect = (selection) => {
    setSelectedTime(selection)
    const startTime = selection.time

    // Check if service has add-ons (add-ons are always on the parent service, not variations)
    const serviceAddOns = service.addOns || []
    if (serviceAddOns.length > 0) {
      // Show add-on selection modal
      setPendingCheckoutTime(startTime)
      setShowAddOnModal(true)
    } else {
      // No add-ons, redirect to checkout directly
      redirectToCheckout(startTime, [bookingServiceId])
    }
  }

  // Function to handle checkout redirection
  const redirectToCheckout = (startTime, serviceIds) => {
    const encodedStartTime = encodeURIComponent(startTime)
    const serviceParam = serviceIds.join(",")

    // Build checkout URL with required parameters
    let checkoutUrl = `/checkout?pid=${practice.id}&sid=${serviceParam}&lid=${selectedLocation.id}&st=${encodedStartTime}`

    // Add theme colors if available (for v2 websites)
    if (theme?.colors?.primary) {
      checkoutUrl += `&primaryColor=${encodeURIComponent(theme.colors.primary)}`
    }
    if (theme?.colors?.textLight) {
      checkoutUrl += `&textColor=${encodeURIComponent(theme.colors.textLight)}`
    }
    if (theme?.borderRadius) {
      checkoutUrl += `&borderRadius=${encodeURIComponent(theme.borderRadius)}`
    }

    // Redirect to checkout page
    window.location.href = checkoutUrl
  }

  // Handler for when the user completes add-on selection and proceeds to checkout
  const handleAddOnCheckout = (serviceIds) => {
    redirectToCheckout(pendingCheckoutTime, serviceIds)
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
      {/* Hero Section with transparent header over it (like /practice-slug/services reference) */}
      <div className="relative" style={{ backgroundColor: theme?.colors?.bgLight || "#f4ece9" }}>
        {/* Header overlays hero - transparent so hero background shows through */}
        <div className="relative inset-x-0 top-0 z-10">
          <HeaderSection
            section={headerSection}
            practice={practice}
            theme={theme}
            deviceType="desktop"
            transparent={true}
            transparentBackgroundColor={theme?.colors?.bgLight || "#f4ece9"}
          />
        </div>

        {/* Hero content - padding-top so content clears header + promo banner (prevents overlap) */}
        <div
          className="relative pb-16 pt-4"
          style={{
            paddingTop: (() => {
              const showPromoBanner = theme?.header?.showPromoBanner || false
              const headerHeight = 100
              const promoBannerHeight = showPromoBanner ? 80 : 0
              return `${headerHeight + promoBannerHeight + 32}px`
            })()
          }}>
          <div className="mx-auto max-w-[1440px] px-8">
            {/* Practitioner & Service Title */}
            <div className="flex flex-col items-center gap-4 px-[390px] py-12 pb-24">
              <div className="flex items-center gap-3">
                <div
                  className="size-12 shrink-0 rounded-full bg-cover bg-center"
                  style={{
                    backgroundImage: practice.user?.profilePhotoUrl ? `url(${practice.user.profilePhotoUrl})` : "none",
                    backgroundColor: practice.user?.profilePhotoUrl ? "transparent" : theme?.colors?.greyLight || "#ccc"
                  }}
                />
                <div className="flex flex-col items-start justify-center gap-0.5">
                  <h2
                    className="text-[18px] font-bold leading-[24px]"
                    style={{
                      fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                      color: theme?.colors?.textDark || "#55433F"
                    }}>
                    {practice.user?.name || practice.name}
                  </h2>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => {
                      const isFilled = i < Math.floor(practice.reviewsAverage || 0)
                      const fillColor = isFilled ? theme?.colors?.reviewStars || "#F8B049" : "#E5E5E5"
                      return (
                        <svg
                          key={i}
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="aspect-square">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.19157 2.14054C7.49046 1.4219 8.50848 1.4219 8.80738 2.14054L10.1954 5.47768L13.7981 5.76651C14.5739 5.8287 14.8885 6.7969 14.2974 7.30323L11.5525 9.65453L12.3911 13.1702C12.5717 13.9272 11.7481 14.5256 11.0839 14.1199L7.99947 12.236L4.91504 14.1199C4.25083 14.5256 3.42723 13.9273 3.60782 13.1702L4.44643 9.65453L1.70153 7.30323C1.11044 6.7969 1.42502 5.8287 2.20085 5.76651L5.80357 5.47768L7.19157 2.14054Z"
                            fill={fillColor}
                          />
                        </svg>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Service Title */}
              <h1
                className="max-w-[660px] text-center text-[56px] font-bold leading-[72px]"
                style={{
                  fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                  color: theme?.colors?.textDark || "#55433f"
                }}>
                {service.name}
              </h1>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <div className="-mb-32 h-20 w-fit" style={{ backgroundColor: theme?.colors?.bgLight || "#f4ece9" }} />
              <div className="absolute bottom-0 left-1/2 w-[872px] -translate-x-1/2">
                <ServiceTimeSearchBar
                  service={service}
                  selectedDuration={selectedDuration}
                  selectedLocation={selectedLocation}
                  onDurationChange={handleDurationChange}
                  onLocationChange={handleLocationChange}
                  hasMultipleDurations={hasMultipleDurations}
                  hasMultipleLocations={hasMultipleLocations}
                  theme={theme}
                  deviceType="desktop"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - White Background Section */}
      <div className="w-full pt-24" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="mx-auto flex max-w-[760px] flex-col gap-8 px-5 pb-48 pt-6">
          {/* Service Details */}
          <ServiceDetails service={service} practice={practice} theme={theme} />

          {/* Available Times */}
          <ServiceAvailableTimes
            availableTimes={allAvailableTimes}
            selectedLocation={selectedLocation}
            loading={loadingAvailability}
            error={availabilityError}
            timeZone={timeZone}
            practice={practice}
            bookingServiceId={bookingServiceId}
            onLoadMore={handleLoadMoreTimes}
            hasMoreData={!!lastDateInPage && !hasReachedMaxMonths}
            loadingMore={loadingMore}
            onTimeSelect={handleTimeSelect}
            selectedTime={selectedTime}
            theme={theme}
          />

          {/* Service Image - preserve aspect to avoid cropping square uploads */}
          {service.imageUrl && (
            <div
              className="relative w-full overflow-hidden rounded-xl"
              style={{
                maxWidth: "720px",
                aspectRatio: "3/2"
              }}>
              <img
                src={service.imageUrl}
                alt={service.name}
                className="size-full object-contain"
                style={{ backgroundColor: theme?.colors?.greyLight || "#f4ece9" }}
              />
            </div>
          )}

          {/* Location */}
          <ServiceLocation
            locations={service.locations}
            selectedLocation={selectedLocation}
            practice={practice}
            theme={theme}
          />

          {/* Practitioner */}
          <ServicePractitioner practice={practice} theme={theme} />
        </div>
      </div>

      {/* Footer */}
      <FooterSection section={footerSection} practice={practice} theme={theme} deviceType="desktop" />

      {/* Add-on selection modal */}
      {service.addOns && service.addOns.length > 0 && (
        <ServiceAddOnModal
          visible={showAddOnModal}
          onClose={() => {
            setShowAddOnModal(false)
            setPendingCheckoutTime(null)
          }}
          service={selectedDuration || service}
          addOns={service.addOns || []}
          onCheckout={handleAddOnCheckout}
          theme={theme}
        />
      )}
    </div>
  )
}

ServicePageV2Desktop.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    practitionerName: PropTypes.string,
    practitionerImage: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      title: PropTypes.string,
      profilePhotoUrl: PropTypes.string
    }),
    reviewsAverage: PropTypes.number,
    reviewsCount: PropTypes.number,
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string
      })
    ]),
    locations: PropTypes.arrayOf(PropTypes.object),
    pages: PropTypes.arrayOf(PropTypes.object),
    socialLinks: PropTypes.object
  }).isRequired,
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    imageUrl: PropTypes.string,
    locations: PropTypes.arrayOf(PropTypes.object),
    variations: PropTypes.arrayOf(PropTypes.object),
    addOns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        amountCents: PropTypes.number.isRequired
      })
    )
  }).isRequired,
  theme: PropTypes.object
}

export default ServicePageV2Desktop
